import React, { useState } from 'react';
import {
  BooleanField,
  Datagrid,
  FunctionField,
  ImageField,
  List,
  TextField,
  TopToolbar,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import { useRecordContext } from 'ra-core';
import { RunaGiftCardsDialog } from './RunaGiftCardsDialog';
import { GiftCardOrderingDialog } from './GiftCardOrderingDialog';
import { EditGiftCardDialog } from './EditGiftCardDialog';
import { deleteJson } from '../../utils/api';

type EditActionProps = {
  handleOpenEditDialog: (record: any) => void;
};

const EditAction: React.FC<EditActionProps> = ({ handleOpenEditDialog }) => {
  const record = useRecordContext();
  return (
    <Button type="button" onClick={() => handleOpenEditDialog(record)}>
      Edit
    </Button>
  );
};

type DeleteActionProps = {
  handleDelete: (record: any) => void;
};

const DeleteAction: React.FC<DeleteActionProps> = ({ handleDelete }) => {
  const record = useRecordContext();
  return (
    <Button type="button" color="error" onClick={() => handleDelete(record)}>
      Delete
    </Button>
  );
};

export const GiftCardList = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openOrderingDialog, setOpenOrderingDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedGiftCard, setSelectedGiftCard] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [giftCardToDelete, setGiftCardToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenOrderingDialog = () => {
    setOpenOrderingDialog(true);
  };

  const handleCloseOrderingDialog = () => {
    setOpenOrderingDialog(false);
  };

  const handleOpenEditDialog = (giftCard: any) => {
    setSelectedGiftCard(giftCard);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedGiftCard(null);
  };

  const handleSaveGiftCard = () => {
    refresh();
  };

  const handleDeleteGiftCard = (giftCard: any) => {
    setGiftCardToDelete(giftCard);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setGiftCardToDelete(null);
  };

  const confirmDeleteGiftCard = async () => {
    if (giftCardToDelete) {
      try {
        await deleteJson(`/admin/gift-card/${giftCardToDelete.id}`);
        notify(`Gift card "${giftCardToDelete.name}" deleted successfully`, {
          type: 'success',
        });
        setOpenDeleteDialog(false);
        setGiftCardToDelete(null);
        refresh();
      } catch (error) {
        notify(`Failed to delete gift card "${giftCardToDelete.name}"`, {
          type: 'error',
        });
      }
    }
  };

  return (
    <>
      <TopToolbar>
        <Button color="primary" variant="contained" onClick={handleOpenDialog}>
          Show Runa Gift Cards
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleOpenOrderingDialog}
        >
          Reorder Gift Cards
        </Button>
      </TopToolbar>

      <RunaGiftCardsDialog
        open={openDialog}
        onClose={handleCloseDialog}
        refresh={refresh}
      />
      <GiftCardOrderingDialog
        open={openOrderingDialog}
        onClose={handleCloseOrderingDialog}
        refresh={refresh}
      />

      <List resource="gift-card" hasCreate={false} exporter={false}>
        <Datagrid bulkActionButtons={false}>
          <FunctionField
            label="Is Visible"
            render={({ isVisible }: any) => (
              <BooleanField
                record={{ isVisible: Boolean(isVisible) }}
                source="isVisible"
              />
            )}
          />
          <ImageField
            source="assets.giftCardUrl"
            className="gift-card-img"
            label="Image"
            headerClassName="head-center"
          />
          <TextField source="name" />
          <TextField source="provider" />
          <FunctionField
            label="Is Orderable"
            render={({ isOrderable }: any) => (
              <BooleanField
                record={{ isOrderable: Boolean(isOrderable) }}
                source="isOrderable"
              />
            )}
          />
          <TextField source="rpRate" />
          <EditAction handleOpenEditDialog={handleOpenEditDialog} />
          <DeleteAction handleDelete={handleDeleteGiftCard} />
        </Datagrid>
      </List>

      {selectedGiftCard && (
        <EditGiftCardDialog
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          giftCard={selectedGiftCard}
          onSave={handleSaveGiftCard}
          refresh={refresh}
        />
      )}

      {giftCardToDelete && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete the gift card "
            {giftCardToDelete.name}"?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button
              onClick={confirmDeleteGiftCard}
              color="primary"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
